import { useMemo, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { Select, SelectItem, Spinner } from "@heroui/react";
import { useS2DashboardContext } from "../context/S2DashboardProvider";
import FundPickerBar from "../components/FundPickerBar";
import { api } from "../services/api";
import { DataTable } from "@/components/data-table";
import PDFModal from "../components/PDFModal";
import { peCol } from "../model/tableHelpers";
import type { PeRow, GroundingState } from "../model/tableHelpers";
import { S2_GROUP_HEADER_CLASS } from "../model/constants";
import { periodKey, findDocForKey } from "../model/utils";

// ─── Column groups (TanStack native nested ColumnDef) ─────────────────────────

function buildFOColumns(
  onSource: (g: GroundingState) => void,
): ColumnDef<PeRow>[] {
  const p = (key: string, label: string, size?: number) =>
    peCol(key, label, { size, onSource });

  return [
    {
      id: "general-information",
      header: "General Information",
      columns: [
        p("fund_overview_fund_name", "Fund Name", 160),
        p("fund_overview_name_of_general_partner", "General Partner"),
        p("fund_overview_manager", "Manager"),
        p("fund_overview_legal_form", "Legal Form"),
        p("fund_overview_fund_investment_state", "Status"),
        p("fund_overview_fund_domicile", "Domicile"),
        p("fund_overview_team_location_country", "Team Location"),
        p("fund_overview_currency_of_the_fund", "Currency"),
        p("fund_overview_reporting_currency_fund", "Reporting Curr."),
      ],
    },
    {
      id: "dates-lifecycle",
      header: "Dates & Lifecycle",
      columns: [
        p("fund_overview_vintage_year", "Vintage Year"),
        p("fund_overview_generation", "Generation"),
        p("fund_overview_commitment_date", "Commitment Date"),
        p("fund_overview_signature_date", "Signature Date"),
        p("fund_overview_first_closing_date", "First Closing"),
        p("fund_overview_final_closing_date", "Final Closing"),
        p("fund_overview_first_investment_date", "First Investment"),
        p("fund_overview_end_of_investment_period", "End of Inv. Period"),
        p("fund_overview_planned_end_date", "Planned End"),
        p("fund_overview_liquidation_date", "Liquidation Date"),
      ],
    },
    {
      id: "strategy-focus",
      header: "Strategy & Focus",
      columns: [
        p("fund_overview_asset_type", "Asset Type"),
        p("fund_overview_product_type", "Product Type"),
        p("fund_overview_strategy_focus", "Strategy"),
        p("fund_overview_sector_focus", "Sector Focus"),
        p("fund_overview_geographic_focus", "Geographic Focus"),
      ],
    },
    {
      id: "other-information",
      header: "Other Information",
      columns: [
        p("fund_overview_pct_of_private_investors", "% Private Inv."),
        p("fund_overview_in_which_closing_joined", "Joined Closing"),
        p("fund_overview_lei_code", "LEI Code"),
      ],
    },
  ];
}

function buildContextGroup(
  onSource: (g: GroundingState) => void,
): ColumnDef<PeRow> {
  const p = (key: string, label: string, size?: number) =>
    peCol(key, label, { size, onSource });

  return {
    id: "context",
    header: "Context",
    columns: [
      p("_fund_label", "Fund", 140),
      p("_reporting_period", "Period", 90),
    ],
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function FundOverviewView() {
  const {
    funds,
    selectedFundIds,
    selectedFundId,
    docMeta,
    foSelectedDocId,
    setFOSelectedDocId,
  } = useS2DashboardContext();

  const [grounding, setGrounding] = useState<GroundingState | null>(null);

  const activeFundIds = useMemo(() => {
    if (selectedFundIds.length > 0) return selectedFundIds;
    return selectedFundId ? [selectedFundId] : [];
  }, [selectedFundIds, selectedFundId]);

  const isMulti = activeFundIds.length > 1;

  // Build columns (stable because setGrounding is stable from useState)
  const columns = useMemo(() => {
    const foGroups = buildFOColumns(setGrounding);
    return isMulti ? [buildContextGroup(setGrounding), ...foGroups] : foGroups;
  }, [isMulti, setGrounding]);

  const pinnedLeft = isMulti
    ? ["_fund_label", "_reporting_period"]
    : ["fund_overview_fund_name"];

  const sourceOptions = useMemo(() => {
    const opts: { key: string; label: string }[] = [];
    if (activeFundIds.length === 0) return opts;

    const fundMaps = activeFundIds.map((fundId) => {
      const fund = funds.find((f) => f.fund_id === fundId);
      const map = new Map<string, string>();
      // Sort so docs with more holdings come first — prefer fund lookthrough docs over CA-only
      const sortedDocIds = [...(fund?.doc_ids ?? [])].sort((a, b) => {
        const aFO = docMeta[a]?.has_fund_overview ? 1 : 0;
        const bFO = docMeta[b]?.has_fund_overview ? 1 : 0;
        return bFO - aFO;
      });
      sortedDocIds.forEach((docId) => {
        const p = docMeta[docId]?.reporting_period;
        if (p) {
          const k = periodKey(p);
          if (k !== "1900_0" && k !== "0_0" && !map.has(k)) map.set(k, docId);
        }
      });
      return map;
    });

    const [first, ...rest] = fundMaps;
    const commonKeys = [...(first?.keys() ?? [])].filter((k) =>
      rest.every((m) => m.has(k)),
    );

    commonKeys.sort((a, b) => {
      const [ya, qa] = a.split("_").map(Number);
      const [yb, qb] = b.split("_").map(Number);
      return yb !== ya ? yb - ya : qb - qa;
    });

    commonKeys.forEach((k) => {
      const labels = [
        ...new Set(
          fundMaps.map((m) => {
            const docId = m.get(k);
            return docId ? (docMeta[docId]?.reporting_period ?? k) : k;
          }),
        ),
      ];
      opts.push({ key: k, label: labels.join(" / ") });
    });

    return opts;
  }, [activeFundIds, funds, docMeta]);

  const defaultKey = sourceOptions[0]?.key ?? "Merge";
  const effectivePeriodKey = foSelectedDocId ?? defaultKey;

  const validKey = sourceOptions.some((o) => o.key === effectivePeriodKey)
    ? effectivePeriodKey
    : defaultKey;
  const validIsMerge = validKey === "Merge";

  const queryConfigs = useMemo(() => {
    return activeFundIds.flatMap((fundId) => {
      const fund = funds.find((f) => f.fund_id === fundId);
      if (!fund) return [];
      const sortedDocIds = [...fund.doc_ids].sort((a, b) => {
        const aH = docMeta[a]?.active_holdings ?? 0;
        const bH = docMeta[b]?.active_holdings ?? 0;
        return (bH ?? 0) - (aH ?? 0);
      });
      const docId = validIsMerge
        ? "Merge"
        : findDocForKey(sortedDocIds, validKey, docMeta);
      if (!docId) return [];
      return [{ fundId, docId }];
    });
  }, [activeFundIds, funds, validIsMerge, validKey, docMeta]);

  const queries = useQueries({
    queries: queryConfigs.map(({ fundId, docId }) => ({
      queryKey: ["fo", fundId, docId],
      queryFn: () => api.getFundOverview(fundId, docId),
      staleTime: 30_000,
    })),
  });

  const isLoading = queries.some((q) => q.isLoading);

  const rows = useMemo<PeRow[]>(() => {
    return queryConfigs.flatMap(({ fundId, docId }, i) => {
      const fund = funds.find((f) => f.fund_id === fundId);
      const fundName = fund?.fund_name || fundId;
      const fundData = queries[i]?.data ?? [];

      if (validIsMerge) {
        const bestRow =
          fundData.length > 0 ? fundData[fundData.length - 1] : null;
        if (!bestRow) return [];
        const rowDocId = bestRow["system_identifiers_doc_id"] || "";
        const period =
          docMeta[rowDocId]?.reporting_period ||
          docMeta[fund?.doc_ids[fund.doc_ids.length - 1] ?? ""]
            ?.reporting_period ||
          "";
        const resolvedDocId =
          rowDocId || fund?.doc_ids[fund.doc_ids.length - 1] || "";
        return [
          {
            ...bestRow,
            _fund_label: fundName,
            _reporting_period: period,
            _doc_id: resolvedDocId,
            _file_name: docMeta[resolvedDocId]?.file_name || "",
          },
        ];
      } else {
        const period = docMeta[docId]?.reporting_period || "";
        return fundData.map((row) => ({
          ...row,
          _fund_label: fundName,
          _reporting_period: period,
          _doc_id: docId,
          _file_name: docMeta[docId]?.file_name || "",
        }));
      }
    });
  }, [queryConfigs, queries, funds, docMeta, validIsMerge]);

  return (
    <div className="flex flex-col gap-4">
      <FundPickerBar mode="single" />

      {activeFundIds.length === 0 ? (
        <div className="flex h-48 items-center justify-center rounded-xl border border-dashed border-gray-200 bg-white text-sm text-gray-400">
          Select one or more funds above
        </div>
      ) : (
        <>
          <div className="flex items-center gap-3">
            {sourceOptions.length > 1 && (
              <div className="w-72">
                <Select
                  label="Source"
                  size="sm"
                  selectedKeys={new Set([validKey])}
                  onSelectionChange={(keys) => {
                    const val = Array.from(keys)[0] as string;
                    setFOSelectedDocId(val);
                  }}
                  aria-label="Select source"
                >
                  {sourceOptions.map((o) => (
                    <SelectItem key={o.key}>{o.label}</SelectItem>
                  ))}
                </Select>
              </div>
            )}
            {isLoading && <Spinner size="sm" />}
            <span className="text-xs text-gray-400">
              {rows.length} record(s) · {activeFundIds.length} fund
              {activeFundIds.length !== 1 ? "s" : ""}
              {validIsMerge ? " · Merge" : ""}
            </span>
          </div>
          {!isLoading && (
            <DataTable<PeRow, unknown>
              table_name="pe-fund-overview"
              columns={columns}
              data={rows}
              pinnedLeft={pinnedLeft}
              enableHorizontalScroll
              groupHeaderClassName={S2_GROUP_HEADER_CLASS}
              pageSize={1000}
            />
          )}
        </>
      )}

      {/* PDF grounding modal — managed by this view */}
      <PDFModal
        isOpen={!!grounding}
        onClose={() => setGrounding(null)}
        docId={grounding?.docId ?? ""}
        fileName={grounding?.fileName}
        page={grounding?.page ?? 1}
        quote={grounding?.quote ?? ""}
        rowLabel={grounding?.rowLabel}
        colLabel={grounding?.colLabel}
        value={grounding?.value}
      />
    </div>
  );
}
